import { z } from 'zod';
import { Id, IsoDate, LawCode, PageQuery, Timestamp, VerticalKey } from './common.js';
import { Section } from './relationships.js';

// ---- Long-term memory (user can see + delete; DPDP) ----
export const MemoryScope = z.enum(['user', 'relationship', 'tenant']);
export const MemoryItem = z.object({
  id: Id,
  scope: MemoryScope,
  userId: Id.nullable(),
  relationshipId: Id.nullable(),
  text: z.string(),
  source: z.enum(['user', 'ai', 'import']),
  binding: z.boolean(),
  createdAt: Timestamp,
});
export type MemoryItem = z.infer<typeof MemoryItem>;
export const MemoryListQuery = PageQuery.extend({ relationshipId: Id.optional(), scope: MemoryScope.optional() });
export type MemoryListQuery = z.infer<typeof MemoryListQuery>;

// ---- Files (private S3, presigned 5 min) ----
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const PRESIGN_TTL_SECONDS = 300;
export const ALLOWED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
] as const;
export const AllowedMime = z.enum(ALLOWED_MIME);

export const PresignUploadRequest = z
  .object({
    relationshipId: Id,
    fileName: z.string().trim().min(1).max(255).refine((n) => !/[\\/\u0000]/.test(n), 'no path separators'),
    mimeType: AllowedMime,
    sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES, 'max 20 MB'),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type PresignUploadRequest = z.infer<typeof PresignUploadRequest>;

export const PresignUploadResponse = z.object({
  fileId: Id,
  url: z.string().url(),
  method: z.literal('PUT'),
  headers: z.record(z.string()),
  expiresInSeconds: z.literal(PRESIGN_TTL_SECONDS),
});
export type PresignUploadResponse = z.infer<typeof PresignUploadResponse>;

export const FileObject = z.object({
  id: Id,
  relationshipId: Id,
  verticalId: Id,
  ownerUserId: Id,
  name: z.string(),
  mimeType: AllowedMime,
  sizeBytes: z.number().int().positive(),
  sha256: z.string(),
  status: z.enum(['pending', 'uploaded']),
  createdAt: Timestamp,
});
export type FileObject = z.infer<typeof FileObject>;

export const PresignDownloadResponse = z.object({
  url: z.string().url(),
  expiresInSeconds: z.literal(PRESIGN_TTL_SECONDS),
});
export type PresignDownloadResponse = z.infer<typeof PresignDownloadResponse>;

// ---- Notifications (internal users only) ----
export const NotificationKind = z.enum(['daily_brief', 'reminder', 'alert', 'reassigned']);
export const Notification = z.object({
  id: Id,
  userId: Id,
  kind: NotificationKind,
  title: z.string(),
  body: z.string(),
  relationshipId: Id.nullable(),
  readAt: Timestamp.nullable(),
  createdAt: Timestamp,
});
export type Notification = z.infer<typeof Notification>;
export const NotificationListQuery = PageQuery.extend({
  unread: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
});

// ---- Today brief ----
export const TodayItemReason = z.enum(['watch_due', 'commitment_due', 'unacked_inbound', 'renewal', 'overdue']);
export const TodayItem = z.object({
  relationshipId: Id,
  contactName: z.string(),
  company: z.string(),
  verticalKey: VerticalKey,
  section: Section,
  reasons: z.array(TodayItemReason).min(1),
  /** Deterministic next lawful move from packages/core. */
  nextMove: z.string(),
  law: LawCode.nullable(),
  draftId: Id.nullable(),
  daysOverdue: z.number().int(),
});
export type TodayItem = z.infer<typeof TodayItem>;
export const TodayQuery = z.object({ vertical: VerticalKey.optional() });
export const TodayBrief = z.object({
  date: IsoDate,
  timezone: z.literal('Asia/Kolkata'),
  userId: Id,
  items: z.array(TodayItem),
  /** AI-written narrative; numbers inside it are injected, never computed by the model. */
  summary: z.string().nullable(),
  generatedAt: Timestamp,
});
export type TodayBrief = z.infer<typeof TodayBrief>;
